import {makeAutoObservable, observable, ObservableMap} from 'mobx'
import {Time} from 'yougile-util'
import {
  ExtensionInfo,
  ExtensionMedia,
  ExtensionRatingValue,
  ExtensionTag,
  Hub,
  HubUtil,
  newsCompanyId
} from 'yougile-model'
import {Sync} from '@client/sync'
import {SyncEvents} from '@client/sync-events'
import {globals} from '@extensions/globals-base'

const getExtensionNewsHub = (extensionId: string): Hub | undefined => {
  const box = Sync.newsSandbox
  const hubTitle = HubUtil.getExtensionCommentsHubTitle(extensionId)
  const hub = box?.findHubByTitle(hubTitle, box.companyId, true)

  return hub
}

export class ExtensionStore {
  private galleryExtStore: ExtensionsStore
  private syncEventsListener: number
  private newsHub: Hub | undefined

  // Extension Info
  id: string
  name: string
  enabled: boolean
  installed: boolean
  ratings: ObservableMap<string, ExtensionRatingValue> = observable.map()
  tags: ExtensionTag[]
  instructionLink: string
  keywords: string[]
  media: ExtensionMedia
  description: string
  dateReleased: Date
  isInstallInProgress = false
  installsCount: number

  // Computed
  commentsCount: number

  constructor(extension: ExtensionInfo, galleryExtStore: ExtensionsStore) {
    this.initFromExtensionInfo(extension)

    this.galleryExtStore = galleryExtStore
    this.newsHub = getExtensionNewsHub(this.id)
    this.syncCommentsCount()

    makeAutoObservable(this, {}, {autoBind: true})

    this.syncEventsListener = SyncEvents.addListener(
      newsCompanyId,
      () => this.newsHub?.id,
      this.syncCommentsCount
    )
  }

  private syncCommentsCount(): void {
    this.commentsCount = this.newsHub?.chat?.getMessagesCount() ?? 0
  }

  initFromExtensionInfo(extension: ExtensionInfo): void {
    this.id = extension.id
    this.name = extension.name ?? `unknown_ext_${extension.id}`
    this.description = extension.description
    this.media = extension.media ?? extension.previews ?? []
    this.enabled = extension.enabled
    this.installed = extension.installed
    this.ratings = observable.map(Object.entries(extension.ratings ?? {}))
    this.tags = extension.tags ?? []
    this.instructionLink = extension.instructionLink ?? undefined
    this.keywords = extension.keywords ?? []
    this.installsCount = extension.installsCount ?? 0

    const dateReleased = new Date(extension.dateReleased)
    this.dateReleased = !isNaN(Number(dateReleased)) ? dateReleased : undefined

    // TODO: This backward compatibility migration should be
    // removed after roll out release of standalone >= v1.96
    if (extension.enabled === undefined && extension.installed !== undefined) {
      // This fallback allows just to correctly display state
      // "Enabled/Disabled" button for this in Extensions Gallery
      // for the data that came from server-side of standalone
      // version that < v1.96 (there "installed" meant as "enabled").
      this.enabled = extension.installed
    }
  }

  rate(rating: ExtensionRatingValue): void {
    this.ratings.set(globals.currentUserId, rating)
  }

  enable(): void {
    this.installed = true
    this.enabled = true
    this.galleryExtStore.updateModified()
  }

  disable(): void {
    this.enabled = false
    this.galleryExtStore.updateModified()
  }

  setInstallProgress(value: boolean): void {
    this.isInstallInProgress = value
  }

  get rating(): number {
    const ratings = Array.from(this.ratings.values())
    const sum = ratings.reduce((sum, v) => sum + v, 0)
    return sum === 0 ? 0 : Math.max(0, sum / ratings.length)
  }

  get currentUserRating(): ExtensionRatingValue {
    return this.ratings.get(globals.currentUserId)
  }

  get ratingsCount(): number {
    return this.ratings.size
  }

  get hasMedia(): boolean {
    return this.media.length > 0
  }

  get hasTags(): boolean {
    return this.enabled || this.tags.length > 0
  }

  get hasInstructionLink(): boolean {
    return Boolean(this.instructionLink)
  }

  get canRate(): boolean {
    return this.installed && !globals.standalone
  }

  get commentsHub(): Hub | undefined {
    return this.newsHub
  }

  dispose(): void {
    SyncEvents.removeListener(this.syncEventsListener)
  }
}

interface ExtensionsStoreState {
  modified: number
}

export class ExtensionsStore {
  state: ExtensionsStoreState = {
    modified: 0
  }

  extensions: ObservableMap<string, ExtensionStore> = observable.map()

  constructor() {
    makeAutoObservable(this, {}, {autoBind: true})
  }

  public init(): void {
    globals.onCompanyChange(this.dispose)
  }

  updateModified(): void {
    this.state.modified = Time.now()
  }

  setExtensions(extensions: ExtensionInfo[]): void {
    for (const ext of extensions) {
      let extStore = this.extensions.get(ext.id)

      if (!extStore) {
        extStore = new ExtensionStore(ext, this)
        this.extensions.set(ext.id, extStore)
      } else {
        extStore.initFromExtensionInfo(ext)
      }
    }

    this.updateModified()
  }

  dispose(): void {
    this.extensions.clear()
    this.extensions.forEach(ext => ext.dispose())
  }
}

export const extensionsStore = new ExtensionsStore()
